import React from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";
import "@/index.css";
import App from "@/App";

// Canonical address: www.<domain> → <domain> (one address for logins, bookmarks and passkeys)
// Skipped inside an installed app (standalone) so a www-installed PWA stays within its own scope.
const { hostname, protocol, port, pathname, search, hash } = window.location;
const isStandaloneApp = (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) || window.navigator.standalone === true;
if (!isStandaloneApp && hostname.startsWith("www.") && hostname.split(".").length >= 3) {
  window.location.replace(`${protocol}//${hostname.slice(4)}${port ? `:${port}` : ""}${pathname}${search}${hash}`);
}

axios.defaults.withCredentials = true;

// Explicit logout → don't auto-open Face ID on the login page that follows
axios.interceptors.request.use((config) => {
  if (/\/(auth|portal)\/logout$/.test(config.url || "")) sessionStorage.setItem("jmepl_skip_auto_face", "1");
  return config;
});

// Auto-recover stale sessions: any 401 outside the login page forces a fresh login
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const reqUrl = error?.config?.url || "";
    const onAuthPage = ["/login", "/forgot-password", "/reset-password", "/portal"].some((p) =>
      window.location.pathname.startsWith(p)
    );
    const isLoginCall = reqUrl.includes("/auth/login") || reqUrl.includes("/portal/login");
    if (status === 401 && !isLoginCall && !onAuthPage) {
      const isPortalApi = reqUrl.includes("/api/portal/");
      if (isPortalApi) {
        localStorage.removeItem("portal_user");
        window.location.href = "/portal";
      } else {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// Register Service Worker for PWA with update banner
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js')
      .then((registration) => {
        const announce = (worker) => {
          if (worker) window.dispatchEvent(new CustomEvent('sw-update-available', { detail: worker }));
        };
        // An update was already downloaded and is waiting
        if (registration.waiting && navigator.serviceWorker.controller) announce(registration.waiting);
        // A new version gets downloaded while the app is open
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              announce(newWorker);
            }
          });
        });
        // Check for a new version every 60 seconds while the app is open
        setInterval(() => registration.update().catch(() => {}), 60000);
      })
      .catch((error) => {
        console.error('Service Worker registration failed:', error);
      });

    // When the new service worker takes control (after user taps Refresh), reload once
    let refreshed = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshed) return;
      refreshed = true;
      window.location.reload();
    });
  });
}
